export type InstitutionalNavSubItem = {
  label: string;
  href: string;
  description: string;
  badge?: string;
};

export type InstitutionalNavFeature = {
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  stats: readonly {
    value: string;
    label: string;
  }[];
  lines: readonly string[];
};

export type InstitutionalNavItem = {
  label: string;
  href: string;
  summary: string;
  // "mega" opens a megamenu panel; "link" is a plain top-level link (no dropdown).
  panel: "mega" | "link";
  subItems: readonly InstitutionalNavSubItem[];
  feature?: InstitutionalNavFeature;
  footer?: {
    label: string;
    href: string;
  };
};

// Capability and situation mapping follows the Zom sitemap (16 June 2026).
// Platform = what Zom is (named capabilities). Solutions = who it is for.
// Paused entries (Skills, Firm-level oversight, Pricing) are intentionally
// omitted. Descriptions are taken from the sitemap menu copy.
export const institutionalNavItems: readonly InstitutionalNavItem[] = [
  {
    label: "Platform",
    href: "/platform",
    summary: "What Zom is: the named capabilities, by what Zom holds or does.",
    panel: "mega",
    subItems: [
      {
        label: "Client",
        href: "/platform/the-thread",
        description:
          "Every life event and financial decision, held in one continuous record over 10 to 20 years. The relationship memory no advisor could hold alone.",
      },
      {
        label: "Portfolio & Risk",
        href: "/platform/the-signal",
        description:
          "What changed, what's at risk, and which clients a new bill or market move actually touches, before they ask.",
      },
      {
        label: "Tax",
        href: "/platform/tax-agent",
        description:
          "Reads the real return, runs the actual tax math, sources every number. A grounded engine, not a chatbot guessing.",
      },
      {
        label: "Productivity",
        href: "/platform/follow-through",
        description:
          "The recurring work around every client, handled in the background.",
      },
      {
        label: "Compliance",
        href: "/platform/the-log",
        description:
          "A clear record of everything reviewed, approved, and acted on.",
      },
    ],
    feature: {
      eyebrow: "One operating view",
      title: "One operating view.",
      body: "Operating intelligence across every client, portfolio, and workflow, built on the systems you already use.",
      ctaLabel: "Explore the platform",
      ctaHref: "/platform",
      stats: [
        { value: "14+", label: "systems mapped" },
        { value: "<11s", label: "brief assembly" },
        { value: "92%", label: "drafts retained" },
      ],
      lines: [
        "Client and portfolio context connected",
        "Tax math grounded and sourced",
        "Every output checked and logged",
      ],
    },
    footer: {
      label: "Integrations, the layer underneath",
      href: "/platform/integrations",
    },
  },
  {
    label: "Solutions",
    href: "/solutions",
    summary: "Personas inside the RIA, pulling several capabilities together.",
    panel: "mega",
    subItems: [
      {
        label: "Grow without the overhead",
        href: "/solutions/grow",
        description:
          "Grow AUM without growing the back office. [Add the next set of households on the team you already have.]",
      },
      {
        label: "Run the firm, not the tools",
        href: "/solutions/operations",
        description:
          "The brief is already there. You didn't build it. [The 8:45 brief is assembled and the firm's open work is triaged before you sit down.]",
      },
      {
        label: "Associate advisor",
        href: "/solutions/associate",
        description:
          "Get the credit for the insight, not the assembly. [The forty-minute prep brief builds itself, so you bring the analysis instead of the busywork.]",
      },
      {
        label: "Firm-level oversight",
        href: "/solutions/oversight",
        description: "See the operating posture across the whole firm.",
      },
    ],
    feature: {
      eyebrow: "Personas",
      title: "Built around the teams inside growing RIAs.",
      body: "For founders, operations leads, and advisor pods carrying growth, service quality, and firm oversight together.",
      ctaLabel: "View solutions",
      ctaHref: "/solutions",
      stats: [
        { value: "5", label: "intelligences" },
        { value: "1", label: "client record" },
        { value: "0", label: "lost context" },
      ],
      lines: [
        "Client picture assembled before it is requested",
        "Actions stay tied to sources and approvals",
        "The firm's best workflow becomes repeatable",
      ],
    },
    footer: { label: "See all solutions", href: "/solutions" },
  },
  {
    label: "Security",
    href: "/security",
    summary: "Controls for AI-assisted work that remains advisor-led.",
    panel: "link",
    subItems: [],
  },
  {
    label: "Company",
    href: "/company",
    summary: "The team and operating posture behind Zom.",
    panel: "link",
    subItems: [],
  },
];

export const systemLogoGrid = [
  { label: "Nitrogen", src: "/images/integration-logos/nitrogen.svg" },
  { label: "Wealthbox", src: "/images/misc-logos/wealthbox.png" },
  { label: "Schwab", src: "/images/integration-logos/schwab.png" },
  { label: "Fidelity", src: "/images/integration-logos/fidelity.png" },
  { label: "Orion", src: "/images/integration-logos/orion.svg" },
  { label: "eMoney", src: "/images/integration-logos/emoney.svg" },
  { label: "Google Drive", src: "/onboarding/integrations/google-drive.png" },
  { label: "Outlook", src: "/onboarding/integrations/outlook.svg" },
  { label: "Slack", src: "/onboarding/integrations/slack.svg" },
  { label: "DocuSign", src: "/onboarding/integrations/docusign.svg" },
  { label: "BridgeFT", src: "/onboarding/integrations/bridgeft.png" },
  { label: "RightCapital", src: "/images/integration-logos/rightcapital.svg" },
] as const;

// Live integrations only (matches platform page INTEGRATIONS status: "live").
export const connectedSystemsBelt = [
  { label: "Wealthbox", src: "/onboarding/integrations/wealthbox.png" },
  { label: "BridgeFT", src: "/onboarding/integrations/bridgeft.png" },
  { label: "Outlook", src: "/onboarding/integrations/outlook.svg" },
  { label: "Gmail", src: "/onboarding/integrations/gmail.png" },
  {
    label: "Google Calendar",
    src: "/onboarding/integrations/google-calendar.png",
  },
  { label: "Google Drive", src: "/onboarding/integrations/google-drive.png" },
] as const;

export const intelligenceSources = [
  {
    label: "Client record",
    title: "Household history",
    body: "Relationship notes, service model, life events, and household context.",
  },
  {
    label: "Custodian",
    title: "Portfolio record",
    body: "Accounts, positions, transactions, cost basis, and allocation drift.",
  },
  {
    label: "Calendar",
    title: "Meeting rhythm",
    body: "Upcoming reviews, follow-ups, approvals, and planning milestones.",
  },
  {
    label: "Files",
    title: "Source documents",
    body: "Statements, planning files, tax artifacts, and policy documents.",
  },
] as const;

export const intelligenceOutputs = [
  "Meeting brief",
  "Risk exception",
  "Action plan",
  "Compliance trail",
] as const;

export const workflowPanels = [
  {
    eyebrow: "01 / Client context",
    title: "Every household, one governed record.",
    body: "Zom assembles client, portfolio, planning, and communication context into a single advisor-ready workspace before the team asks for it.",
    meta: ["Client record", "Custodian", "Email", "Planning"],
  },
  {
    eyebrow: "02 / Advisor questions",
    title: "Plain-language analysis with source-linked answers.",
    body: "Ask about drift, exposure, cash needs, meeting prep, or next best action. Zom returns the answer with the evidence needed for review.",
    meta: ["Portfolio", "Risk", "Notes", "Policy"],
  },
  {
    eyebrow: "03 / Follow-through",
    title: "From insight to action without losing control.",
    body: "Tasks, client record updates, draft emails, and compliance artifacts stay attached to the originating evidence and approval path.",
    meta: ["Tasks", "Client record", "Email", "Audit"],
  },
] as const;

// Card titles/bodies have no v3 HTML source, so they are bracketed as AI
// drafts. The command + trace lines are illustrative product mock data.
export const useCases = [
  {
    id: "prep",
    label: "Meeting prep",
    title: "[Turn scattered client history into a review-ready brief.]",
    body: "[Before every meeting, Zom brings together portfolio movement, open items, previous notes, and planning context so the advisor starts with the full picture.]",
    command: "Prepare Smith Family annual review",
    lines: [
      "Pulled 4 accounts from BridgeFT",
      "Found 3 planning changes since last review",
      "Drafted agenda and follow-up checklist",
    ],
  },
  {
    id: "risk",
    label: "Portfolio risk",
    title: "[Spot exceptions before they become client issues.]",
    body: "[Zom surfaces concentration, income, tax, and policy exceptions with enough context for an advisor to decide, not just react.]",
    command: "Find households over policy equity bands",
    lines: [
      "12 households evaluated",
      "3 outside target allocation",
      "2 need advisor approval before rebalance",
    ],
  },
  {
    id: "ops",
    label: "Operations",
    title: "[Convert advice into controlled follow-through.]",
    body: "[Meeting outcomes become task lists, client record updates, draft emails, and compliance records without turning the advisor into a project manager.]",
    command: "Close out post-meeting actions",
    lines: [
      "Created 5 tasks from meeting notes",
      "Updated household profile fields",
      "Attached source trail for review",
    ],
  },
] as const;

export const proofMetrics = [
  { label: "Sources unified", value: "Mapped", note: "Firm-approved systems" },
  {
    label: "Review surfaces",
    value: "Advisor-led",
    note: "Human review paths",
  },
  { label: "Audit checkpoints", value: "Traceable", note: "Per action path" },
] as const;

export const securityPrinciples = [
  "Single-tenant deployment posture",
  "Encrypted in transit and at rest",
  "Role-aware access controls",
  "Traceable advisor review paths",
] as const;
